import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Col, Container, Form, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import {
  getVirtualGifts,
  getMyVirtualGifts,
  getGreetingCards,
  getMyGreetingCards,
  purchaseGift
} from '../../../Services/User/GiftStore';
import { showToast } from '../../../Components/Toast/Toast';
import CoinIcon from '../../../assets/images/coin.svg?react';

import InnerBanner from '../../../Components/InnerBanner/InnerBanner';
import CustomButton from '../../../Components/CustomButton';
import { FaGift } from 'react-icons/fa';
import './GiftStore.css';

const GiftStore = () => {
  usePageTitle('Gift Store');
  const [giftType, setGiftType] = useState('stickers');
  const [activeTab, setActiveTab] = useState('buy');
  const [quantities, setQuantities] = useState({});
  const [purchasingId, setPurchasingId] = useState(null);

  // Queries for stickers
  const { data: virtualGifts, isLoading: isLoadingVirtual } = useQuery({
    queryKey: ['virtual-gifts'],
    queryFn: getVirtualGifts,
    enabled: giftType === 'stickers' && activeTab === 'buy',
  });

  const { data: myVirtualGifts, isLoading: isLoadingMyVirtual } = useQuery({
    queryKey: ['my-virtual-gifts'],
    queryFn: getMyVirtualGifts,
    enabled: giftType === 'stickers' && activeTab === 'my',
  });

  // Queries for greeting cards
  const { data: greetingCards, isLoading: isLoadingCards } = useQuery({
    queryKey: ['greeting-cards'],
    queryFn: getGreetingCards,
    enabled: giftType === 'cards' && activeTab === 'buy',
  });

  const { data: myGreetingCards, isLoading: isLoadingMyCards } = useQuery({
    queryKey: ['my-greeting-cards'],
    queryFn: getMyGreetingCards,
    enabled: giftType === 'cards' && activeTab === 'my',
  });

  const getQuantity = (giftId) => quantities[giftId] || 1;

  const handleIncrement = (giftId) => {
    setQuantities(prev => ({
      ...prev,
      [giftId]: (prev[giftId] || 1) + 1
    }));
  };

  const handleDecrement = (giftId) => {
    setQuantities(prev => ({
      ...prev,
      [giftId]: Math.max(1, (prev[giftId] || 1) - 1)
    }));
  };

  const handleBuyGift = async (gift) => {
    try {
      setPurchasingId(gift.id);
      const quantity = getQuantity(gift.id);

      const response = await purchaseGift(gift.id, quantity);

      if (response?.status) {
        showToast(`Successfully purchased ${gift.title}!`, 'success');
        // Reset quantity
        setQuantities(prev => ({ ...prev, [gift.id]: 1 }));
      } else {
        showToast(response?.message || 'Purchase failed', 'error');
      }
    } catch (error) {
      console.error('Error purchasing gift:', error);
      showToast(error?.message || 'Purchase failed', 'error');
    } finally {
      setPurchasingId(null);
    }
  };

  const renderGiftCard = (item, showBuyButton = true) => {
    // Handle both response structures: direct gift object or nested gift.gift
    const gift = item.gift || item;
    const giftId = showBuyButton ? gift.id : item.id;
    const ownedQuantity = !showBuyButton ? (item.quantity || 1) : null;

    const quantity = getQuantity(gift.id);
    const totalCoins = gift.coins * quantity;
    const isPurchasing = purchasingId === gift.id;

    return (
      <Col key={giftId} xs={12} sm={6} md={4} className="mb-4">
        <div className="gift-card">
          <div className="gift-image-wrapper">
            <img
              src={gift.image?.path}
              alt={gift.title}
              className="gift-image"
            />
          </div>

          <div className="gift-content">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="gift-title mb-0 pb-0">{gift.title}</h4>
              {!showBuyButton && ownedQuantity && (
                <div className="gift-owned-badge">{ownedQuantity}</div>
              )}
            </div>

            {showBuyButton && (
              <>
                <div className="gift-coins-row">
                  <div className="gift-coins-display">
                    <span className="gift-coins-label">Coins {totalCoins}</span>
                    <CoinIcon className="coin-icon-small" />
                  </div>

                  <div className="gift-quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => handleDecrement(gift.id)}
                      disabled={isPurchasing}
                    >
                      -
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => handleIncrement(gift.id)}
                      disabled={isPurchasing}
                    >
                      +
                    </button>
                  </div>
                </div>

                <p className="gift-description">{gift.description}</p>

                <CustomButton
                  onClick={() => handleBuyGift(gift)}
                  disabled={isPurchasing}
                  loading={isPurchasing}
                  className="gift-buy-btn"
                >
                  <FaGift className="me-2" />
                  {isPurchasing ? 'Processing...' : 'Buy'}
                </CustomButton>
              </>
            )}

            {!showBuyButton && (
              <>
                <div className="gift-coins-display mb-3">
                  <span className="gift-coins-label">Coins {gift.coins}</span>
                  <CoinIcon className="coin-icon-small" />
                </div>
                <p className="gift-description">{gift.description}</p>
              </>
            )}
          </div>
        </div>
      </Col>
    );
  };

  const renderGiftList = (data, isLoading, showBuyButton = true) => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading...</p>
        </div>
      );
    }

    if (!data?.data || data.data.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="mt-3">No gifts available</p>
        </div>
      );
    }

    return (
      <Row className="justify-content-center">
        {data.data.map((gift) => renderGiftCard(gift, showBuyButton))}
      </Row>
    );
  };

  return (
    <>
      <InnerBanner heading="Gift Store" />
      <section className="py-sm-5 py-4">
        <Container>
          <Row className="mb-4">
            <Col xs={12} md={4} lg={3}>
              <Form.Select
                value={giftType}
                onChange={(e) => {
                  setGiftType(e.target.value);
                  setActiveTab('buy');
                }}
                className="gift-type-select"
              >
                <option value="stickers">Stickers</option>
                <option value="cards">Greeting Cards</option>
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav variant="pills" className="gift-tabs mb-4">
                  <Nav.Item>
                    <Nav.Link eventKey="buy">
                      Buy {giftType === 'stickers' ? 'Stickers' : 'Greeting Cards'}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="my">
                      My {giftType === 'stickers' ? 'Stickers' : 'Greeting Cards'}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="buy">
                    {giftType === 'stickers'
                      ? renderGiftList(virtualGifts, isLoadingVirtual, true)
                      : renderGiftList(greetingCards, isLoadingCards, true)
                    }
                  </Tab.Pane>

                  <Tab.Pane eventKey="my">
                    {giftType === 'stickers'
                      ? renderGiftList(myVirtualGifts, isLoadingMyVirtual, false)
                      : renderGiftList(myGreetingCards, isLoadingMyCards, false)
                    }
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default GiftStore;
